import React, { useState, useEffect, useCallback, useRef } from 'react';
import Select, { type StylesConfig } from 'react-select';
import Papa from 'papaparse';
import ImportService from '@services/ImportService';
import './ImportFromAsana.css';

// ─── Types ────────────────────────────────────────────────────────────────────

interface GroupLayer {
  LayerId: number;
  Name: string;
  Color: string;
  IsEnabled: boolean;
  IsDefault: boolean;
}

interface Group {
  GroupId: number;
  Name: string;
  GroupLayers: GroupLayer[];
}

interface GroupOption {
  value: number;
  label: string;
  groupLayers: GroupLayer[];
}

interface LayerOption {
  value: number;
  label: string;
}

// ─── Select styles ────────────────────────────────────────────────────────────

const buildSelectStyles = (isDark: boolean): StylesConfig<GroupOption | LayerOption, true> => ({
  control: base => ({
    ...base,
    minHeight: 30,
    fontSize: 11,
    borderColor: isDark ? '#4a4c4c' : '#ccc',
    backgroundColor: isDark ? '#3a3c3c' : base.backgroundColor,
    boxShadow: 'none',
    borderRadius: 4,
    '&:hover': { borderColor: isDark ? '#6e6860' : '#aaa' },
  }),
  menu: base => ({
    ...base,
    fontSize: 11,
    zIndex: 9999,
    borderRadius: 2,
    marginTop: 2,
    backgroundColor: isDark ? '#333535' : base.backgroundColor,
  }),
  menuList: base => ({ ...base, padding: 0 }),
  valueContainer: base => ({ ...base, padding: '2px 6px', gap: 2 }),
  indicatorsContainer: base => ({ ...base, height: 30 }),
  indicatorSeparator: () => ({ display: 'none' }),
  clearIndicator: base => ({
    ...base,
    padding: '0 6px',
    color: isDark ? '#6e6860' : '#999',
    cursor: 'pointer',
  }),
  dropdownIndicator: base => ({ ...base, padding: '0 6px', color: isDark ? '#6e6860' : '#999' }),
  multiValue: base => ({
    ...base,
    backgroundColor: isDark ? '#3a3c3c' : '#fff',
    border: `1px solid ${isDark ? '#4a4c4c' : '#ccc'}`,
    borderRadius: 3,
    margin: '1px 2px',
  }),
  multiValueLabel: base => ({
    ...base,
    fontSize: 11,
    color: isDark ? '#a9a093' : '#333',
    padding: '1px 4px',
  }),
  multiValueRemove: base => ({
    ...base,
    color: isDark ? '#6e6860' : '#888',
    padding: '0 3px',
    ':hover': {
      backgroundColor: isDark ? '#3a3c3c' : '#e8e8e8',
      color: isDark ? '#a9a093' : '#555',
    },
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor:
      state.isSelected || state.isFocused ? '#62accb' : isDark ? '#333535' : 'transparent',
    color: state.isSelected || state.isFocused ? '#fff' : isDark ? '#a9a093' : '#333',
    fontSize: 11,
    padding: '5px 10px',
    cursor: 'pointer',
    ':active': { backgroundColor: '#508ca6', color: '#fff' },
  }),
  input: base => ({ ...base, color: isDark ? '#a9a093' : base.color }),
  placeholder: base => ({ ...base, color: isDark ? '#6e6860' : base.color }),
  singleValue: base => ({ ...base, color: isDark ? '#a9a093' : base.color }),
});

// ─── CSV transform (Asana format) ─────────────────────────────────────────────

function transformAsanaData(data: string[][]): Record<string, string>[]
{
  if (!data || data.length < 2) return [];

  // Build header map (strip spaces, rename CreatedAt → TaskCreatedAt)
  const headers = data[0]!.map(h =>
  {
    const clean = h.replace(/\s/g, '');
    return clean === 'CreatedAt' ? 'TaskCreatedAt' : clean;
  });

  // Remove header row; also drop empty trailing rows
  const rows = data.slice(1).filter(r => r.some(cell => cell.trim() !== ''));

  return rows.map(cells =>
  {
    const row: Record<string, string> = {};
    headers.forEach((header, i) =>
    {
      let cell = cells[i] ?? '';
      if (cell.length > 500) cell = cell.substring(0, 500);
      row[header] = cell;
    });
    return row;
  });
}

// ─── Component ────────────────────────────────────────────────────────────────

const ImportFromAsana: React.FC = () =>
{
  const isDark = document.documentElement.classList.contains('dark');
  const selectStyles = buildSelectStyles(isDark);

  const [groupOptions, setGroupOptions] = useState<GroupOption[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<GroupOption[]>([]);
  const [layerOptions, setLayerOptions] = useState<LayerOption[]>([]);
  const [selectedLayers, setSelectedLayers] = useState<LayerOption[]>([]);

  const [parsedData, setParsedData] = useState<Record<string, string>[]>([]);
  const [importDisabled, setImportDisabled] = useState(true);
  const [importLoading, setImportLoading] = useState(false);
  const [importMessage, setImportMessage] = useState('');

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load groups on mount
  useEffect(() =>
  {
    ImportService.getGroups()
      .then((data: Group[]) =>
      {
        setGroupOptions(
          (data || []).map(g => ({
            value: g.GroupId,
            label: g.Name,
            groupLayers: g.GroupLayers || [],
          })),
        );
      })
      .catch(() => {});
  }, []);

  // Recompute import button state
  const checkImportReady = useCallback(
    (groups: GroupOption[], layers: LayerOption[], data: Record<string, string>[]) =>
    {
      const layerIds = layers
        .map(l => l.value)
        .join(',')
        .trim();
      const groupIds = groups
        .map(g => g.value)
        .join(',')
        .trim();
      setImportDisabled(!(layerIds.length > 0 && groupIds.length > 0 && data.length > 0));
    },
    [],
  );

  const handleGroupChange = useCallback(
    (selected: readonly GroupOption[]) =>
    {
      const groups = (selected as GroupOption[]) || [];

      // Derive unique layers from selected groups
      const layerMap = new Map<number, GroupLayer>();
      for (const g of groups)
      {
        for (const l of g.groupLayers)
        {
          if (!layerMap.has(l.LayerId)) layerMap.set(l.LayerId, l);
        }
      }
      const layers = Array.from(layerMap.values()).map(l => ({ value: l.LayerId, label: l.Name }));

      setSelectedGroups(groups);
      setSelectedLayers([]);
      setLayerOptions(layers);
      checkImportReady(groups, [], parsedData);
    },
    [parsedData, checkImportReady],
  );

  const handleLayerChange = useCallback(
    (selected: readonly LayerOption[]) =>
    {
      const layers = (selected as LayerOption[]) || [];
      setSelectedLayers(layers);
      checkImportReady(selectedGroups, layers, parsedData);
    },
    [selectedGroups, parsedData, checkImportReady],
  );

  const handleFileUpload = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) =>
    {
      const file = e.target.files?.[0];
      if (!file) return;

      Papa.parse<string[]>(file, {
        complete: result =>
        {
          if (result.data && result.data.length > 0)
          {
            const transformed = transformAsanaData(result.data);
            setParsedData(transformed);
            checkImportReady(selectedGroups, selectedLayers, transformed);
          }
          else
          {
            setImportMessage('An error occurred trying to import this file.');
          }
        },
      });
    },
    [selectedGroups, selectedLayers, checkImportReady],
  );

  const handleImport = useCallback(async () =>
  {
    if (parsedData.length === 0) return;

    setImportLoading(true);
    setImportMessage('');

    try
    {
      const payload = {
        listAsanaImport: parsedData,
        LayerIds: selectedLayers.map(l => l.value).join(','),
        GroupIds: selectedGroups.map(g => g.value).join(','),
      };

      const res = await ImportService.importFromAsana(payload);
      const message = Array.isArray(res) ? res.join(', ') : 'An error occurred, please try later.';

      // Reset form
      setSelectedGroups([]);
      setSelectedLayers([]);
      setLayerOptions([]);
      setParsedData([]);
      setImportDisabled(true);
      if (fileInputRef.current) fileInputRef.current.value = '';

      setImportMessage(message);
    }
    catch
    {
      setImportMessage('An error occurred, please try later.');
    }

    setImportLoading(false);
  }, [parsedData, selectedLayers, selectedGroups]);

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div id="ImportFromAsanaPage" className="overflow-y-auto">
      <h3>Import from Asana</h3>

      <div className="ifa-panel">
        {/* Important notice */}
        <div className="ifa-notice">
          <strong>Important:</strong> Please verify that project name does not contain comma.
        </div>

        {/* Step 1 */}
        <div className="ifa-step">
          <strong>Step 1:</strong> Choose a file to import (csv).
          <input
            ref={fileInputRef}
            type="file"
            id="inputFileId"
            name="file"
            accept=".csv"
            className="ifa-file-input"
            onChange={handleFileUpload}
          />
        </div>

        {/* Step 2 */}
        <div className="ifa-step">
          <strong>Step 2:</strong> Select security groups and layers where this information will be
          imported.
        </div>

        <div className="ifa-field">
          <span className="ifa-field-label">Security Groups</span>
          <div className="ifa-field-control">
            <Select<GroupOption, true>
              isMulti
              options={groupOptions}
              value={selectedGroups}
              onChange={handleGroupChange}
              placeholder="Select..."
              styles={selectStyles as StylesConfig<GroupOption, true>}
              closeMenuOnSelect={false}
            />
          </div>
        </div>

        <div className="ifa-field">
          <span className="ifa-field-label">Layers</span>
          <div className="ifa-field-control">
            <Select<LayerOption, true>
              isMulti
              options={layerOptions}
              value={selectedLayers}
              onChange={handleLayerChange}
              placeholder="Select..."
              styles={selectStyles as StylesConfig<LayerOption, true>}
              closeMenuOnSelect={false}
              isDisabled={layerOptions.length === 0}
            />
          </div>
        </div>

        {/* Actions */}
        <div className="ifa-actions">
          {importLoading ? (
            <div className="ifa-spinner">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 50 50">
                <path
                  fill="#62accb"
                  d="M25.251 6.461c-10.318 0-18.683 8.365-18.683 18.683h4.068c0-8.071 6.543-14.615 14.615-14.615V6.461z"
                >
                  <animateTransform
                    attributeName="transform"
                    attributeType="xml"
                    dur="0.6s"
                    from="0 25 25"
                    to="360 25 25"
                    type="rotate"
                    repeatCount="indefinite"
                  />
                </path>
              </svg>
            </div>
          ) : (
            importMessage && <span className="ifa-feedback">{importMessage}</span>
          )}
          <button
            className="ifa-import-btn"
            disabled={importDisabled || importLoading}
            onClick={handleImport}
          >
            Import
          </button>
        </div>

        {/* Warning */}
        <div className="ifa-warning">
          <strong>Warning:</strong> Due date field will be saved in the layers selected if the
          custom field exists.
        </div>
      </div>
    </div>
  );
};

export default ImportFromAsana;
